import { usePrevious } from '@mantine/hooks'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { translate } from 'i18n/i18n'
import type { Moment } from 'moment-timezone'
import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'
import type { Contact, Group } from '../../../ContactsAndGroups/ContactsAndGroupsContext'
import { useGroupsAndContacts } from '../../../ContactsAndGroups/ContactsAndGroupsContext'
import type { AlertTypeId, AlertWithPossibleId, Rule } from '../../_types'
import { AlertSection } from '../../components/AlertSection'
import { AlertsSettings } from '../../settings/alertSettings'
import type { convertAPIAlertToInitivalValues } from '../_utils/convertAPIAlertToInitivalValues'
import { getAlertVariablesForQuery } from '../_utils/getAlertVariablesForQuery'
import { DefaultSettingsButton } from '../DefaultSettingsButton/DefaultSettingsButton'
import type { SleepUnitsType } from '../ModalDrawerCreateOrEditAlert'
import { AutomatedScheduling } from './AutomatedScheduling/AutomatedScheduling'
import { Contacts } from './Contacts/Contacts'
import { Coverage } from './Coverage/Coverage'
import { Frequency } from './Frequency/Frequency'
import { Name } from './Name/Name'
import { Scheduling } from './Scheduling/Scheduling'
import { Sleep } from './Sleep/Sleep'
import { Thresholds } from './Thresholds/Thresholds'

type NonWeatherForecastAlertFormValues = Exclude<
  ReturnType<typeof convertAPIAlertToInitivalValues>,
  { alertTypeId: 'weatherForecastAlert' }
>

export const V3AlertForm = ({
  alertId,
  onClose,
  initialState,
  selectedAlert,
  alertTypeId,
  insectId,
  setInsectId,
  setIsSubmitting,
  setSubmitHandler,
  refreshAlerts,
}: {
  opened: boolean
  alertId: string | null
  onClose: () => void
  initialState: NonWeatherForecastAlertFormValues
  selectedAlert: AlertWithPossibleId | null
  alertTypeId: AlertTypeId
  insectId: number | null
  setInsectId: (insectId: number | null) => void
  submitting: boolean
  setIsSubmitting: (isSubmitting: boolean) => void
  setSubmitHandler: (handler: () => unknown) => void
  refreshAlerts: () => void
}) => {
  const [firstRenderComplete, setFirstRenderComplete] = useState(false)
  // STATE FOR Threshold
  const [thresholdIsValid, setThresholdIsValid] = useState(false)
  const [rules, setRules] = useState<Rule[]>(initialState.rules)
  // STATE FOR Coverage
  const [coverageIsValid, setCoverageIsValid] = useState(false)
  const [coverageProperties, setCoverageProperties] = useState<number[]>(initialState.coverageProperties)
  const [coverageStations, setCoverageStations] = useState<string[]>(initialState.coverageStations)
  const [coverageBlocks, setCoverageBlocks] = useState<string[]>(initialState.coverageBlocks)
  const [coverageType, setCoverageType] = useState(initialState.coverageType)
  // STATE FOR Frequency
  const [frequencyMinutes, setFrequencyMinutes] = useState<number | null>(initialState.frequencyMinutes)
  // STATE FOR Sleep
  const [sleepIsValid, setSleepIsValid] = useState(true)
  const [contactsIsValid, setContactsIsValid] = useState(true)
  const [sleepMinutes, setSleepMinutes] = useState<number | null>(initialState.sleepMinutes)
  const [sleep, setSleep] = useState<boolean>(!!initialState.sleepMinutes) // whether or not the alert will use the sleepMinutes in the configuration
  const [sleepUnits, setSleepUnits] = useState<SleepUnitsType>(initialState.sleepUnits) // whether or not the alert will use the sleepMinutes in the configuration
  // STATE FOR Scheduling
  const [scheduleIsValid, setScheduleIsValid] = useState(true)

  const [limitedSchedule, setLimitedSchedule] = useState(
    !!(selectedAlert && selectedAlert.startDate && selectedAlert.endDate),
  )

  const [endDate, setEndDate] = useState<Moment | null>(initialState.endDate)
  const [startDate, setStartDate] = useState<Moment | null>(initialState.startDate)
  // STATE FOR Contacts
  const [contacts, setContacts] = useState<Contact[]>(initialState.contacts)
  const [groups, setGroups] = useState<Group[]>(initialState.groups)
  const [notifyOwnerByEmail, setNotifyOwnerByEmail] = useState(initialState.notifyOwnerByEmail)
  const [notifyOwnerBySMS, setNotifyOwnerBySMS] = useState(initialState.notifyOwnerBySMS)
  // STATE FOR Name
  const [nameIsValid, setNameIsValid] = useState(false)
  const [name, setName] = useState<string | null>(initialState.name)
  // STATE FOR KEEPING TRACK OF WHETHER OR NOT WE SENT IT YET
  const [triedToSubmit, setTriedToSubmit] = useState(false)
  const { groups: allGroups, contacts: allContacts } = useGroupsAndContacts()
  const rulesHaveForecast = !!rules.find((r) => r.key.toLowerCase().includes('forecast'))

  useEffect(() => {
    setFirstRenderComplete(true)
  }, [])

  useEffect(() => {
    const updatedGroups = groups
      .map((group) => {
        const groupInAllGroups = allGroups.find((g) => g.id === group.id)

        if (groupInAllGroups) return { ...groupInAllGroups, alertsContactMethods: group.alertsContactMethods }

        return null
      })
      .filter((g) => g !== null)

    const updatedContacts = contacts
      .map((contact) => {
        const contactInAllContacts = allContacts.find((c) => c.id === contact.id)

        if (contactInAllContacts)
          return { ...contactInAllContacts, alertsContactMethods: contact.alertsContactMethods }

        return null
      })
      .filter((c) => c !== null)

    setGroups(updatedGroups)

    setContacts(updatedContacts)
  }, [allContacts, allGroups])

  useEffect(() => {
    if (!alertTypeId) return

    if (
      rulesHaveForecast &&
      firstRenderComplete &&
      (alertTypeId === 'degreeDays' || alertTypeId === 'fireBlight')
    ) {
      setFrequencyMinutes(AlertsSettings.frequencyMinutesDefault[`${alertTypeId}Forecast`])
    } else setFrequencyMinutes(AlertsSettings.frequencyMinutesDefault[alertTypeId])
  }, [rulesHaveForecast])

  useEffect(() => {
    setInsectId(initialState.insectId)

    setRules(initialState.rules)

    setCoverageProperties(initialState.coverageProperties)

    setCoverageStations(initialState.coverageStations)

    setCoverageBlocks(initialState.coverageBlocks)

    setCoverageType(initialState.coverageType)

    setFrequencyMinutes(initialState.frequencyMinutes)

    setSleepMinutes(initialState.sleepMinutes)

    setSleep(!!initialState.sleepMinutes)

    setSleepUnits(initialState.sleepUnits)

    setEndDate(initialState.endDate)

    setStartDate(initialState.startDate)

    setContacts(initialState.contacts)

    setGroups(initialState.groups)

    setNotifyOwnerByEmail(initialState.notifyOwnerByEmail)

    setNotifyOwnerBySMS(initialState.notifyOwnerBySMS)

    setName(initialState.name)

    setLimitedSchedule(!!(selectedAlert && initialState.startDate && initialState.endDate))
  }, [initialState])

  const previousAlertTypeId = usePrevious(alertTypeId)

  useEffect(() => {
    const shouldReset = previousAlertTypeId && previousAlertTypeId !== alertTypeId

    if (!shouldReset) return

    // handle insect
    setInsectId(null)

    // unvalidate the alert and reset its rules
    setRules([])

    setThresholdIsValid(false)

    // reset the coverage
    setCoverageProperties([])

    setCoverageStations([])

    setCoverageBlocks([])

    setCoverageType('all')

    setCoverageIsValid(true)

    setContactsIsValid(true)

    setName(null)

    // reset the frequencyMinutes
    setFrequencyMinutes(AlertsSettings.frequencyMinutesDefault[alertTypeId])

    // handle sleep
    if (AlertsSettings.allowSleep[alertTypeId]) {
      let newSleepUnits: SleepUnitsType = 'minutes'

      if (AlertsSettings.frequencyMinutesDefault[alertTypeId] === 60) newSleepUnits = 'hours'

      if (AlertsSettings.frequencyMinutesDefault[alertTypeId] === 1440) newSleepUnits = 'days'

      if (newSleepUnits !== sleepUnits) {
        setSleepMinutes(null)

        setSleepUnits(newSleepUnits)
      }
    } else {
      setSleep(false)

      setSleepMinutes(null)

      setSleepIsValid(true)
    }

    if (!AlertsSettings.allowScheduling[alertTypeId]) {
      setLimitedSchedule(false)

      setScheduleIsValid(true)
    }

    setIsSubmitting(false)
  }, [alertTypeId, previousAlertTypeId])

  const requiresInsectId = alertTypeId && AlertsSettings.alertTypesWithPestTypes[alertTypeId]
  const hasTypeAndPest = alertTypeId && (!requiresInsectId || insectId)
  const showThreshold = hasTypeAndPest
  const showCoverage = hasTypeAndPest

  const showFrequency =
    hasTypeAndPest && AlertsSettings.frequencyMinutesOptions[alertTypeId].length && !rulesHaveForecast

  const showAutomatedScheduling = hasTypeAndPest && AlertsSettings.automatedScheduling[alertTypeId]
  const showSleep = hasTypeAndPest && AlertsSettings.allowSleep[alertTypeId]
  const showScheduling = hasTypeAndPest && AlertsSettings.allowScheduling[alertTypeId]
  const showContacts = !!hasTypeAndPest
  const showName = !!hasTypeAndPest

  const handleSubmit = useCallback(async () => {
    const configurationIsValid =
      thresholdIsValid &&
      coverageIsValid &&
      scheduleIsValid &&
      sleepIsValid &&
      contactsIsValid &&
      nameIsValid &&
      alertTypeId &&
      name

    if (!triedToSubmit && !configurationIsValid) setTriedToSubmit(true)

    if (configurationIsValid) {
      const variables = getAlertVariablesForQuery({
        alertTypeId,
        coverageBlocks,
        coverageProperties,
        coverageStations,
        coverageType,
        endDate: limitedSchedule && startDate && endDate ? endDate : null,
        frequencyMinutes,
        groups,
        name,
        contacts,
        notifyOwnerByEmail,
        notifyOwnerBySMS,
        insectId,
        rules: rules.map(({ key, operator, unit, value }) => ({
          key,
          operator,
          unit,
          value: String(value),
        })),
        sleepMinutes: sleep && sleepMinutes ? sleepMinutes : null,
        startDate: limitedSchedule && startDate && endDate ? startDate : null,
      })

      const body: routes.LegacyV3AlertsManagement.Request = {}

      if (alertId) {
        body.legacyV3AlertsToUpdate = {
          [alertId]: variables,
        }
      } else {
        body.legacyV3AlertsToCreate = [variables]
      }

      const successText = !alertId
        ? translate.phrases.banyanApp('Alert successfully created')
        : translate.phrases.banyanApp('Alert successfully updated')

      const errorText = !alertId
        ? translate.phrases.banyanApp('Failed to create alert')
        : translate.phrases.banyanApp('Failed to update alert')

      setIsSubmitting(true)

      try {
        const response = await apiFetch({
          url: '/legacy-v3-alerts-management',
          body,
        })

        const { data, errors } = response
        const newAlertId = alertId ?? Object.keys(data.legacyV3AlertsToCreate ?? {})[0]

        if ((errors && errors.length) || !newAlertId) {
          showNotification({
            type: 'error',
            message: errorText,
          })
        } else {
          showNotification({
            type: 'success',
            message: successText,
          })

          refreshAlerts()
        }

        onClose()
      } catch (error) {
        showNotification({
          type: 'error',
          message: errorText,
        })

        onClose()
      }

      setIsSubmitting(false)
    }
  }, [
    thresholdIsValid,
    coverageIsValid,
    scheduleIsValid,
    sleepIsValid,
    contactsIsValid,
    nameIsValid,
    alertTypeId,
    name,
    coverageBlocks,
    coverageProperties,
    coverageStations,
    coverageType,
    limitedSchedule,
    startDate,
    endDate,
    frequencyMinutes,
    groups,
    contacts,
    notifyOwnerByEmail,
    notifyOwnerBySMS,
    insectId,
    rules,
    sleep,
    sleepMinutes,
    alertId,
  ])

  useEffect(() => {
    setSubmitHandler(() => handleSubmit)
  }, [handleSubmit])

  return (
    <>
      <AlertSection css={{ paddingTop: 0 }}>
        <DefaultSettingsButton
          alertTypeId={alertTypeId}
          insectId={insectId}
          hasTypeAndPest={!!hasTypeAndPest}
          sleep={sleep}
          sleepMinutes={sleepMinutes}
          startDate={startDate}
          endDate={endDate}
          frequencyMinutes={frequencyMinutes}
          limitedSchedule={limitedSchedule}
          rules={rules}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          setFrequencyMinutes={setFrequencyMinutes}
          setRules={setRules}
          setSleep={setSleep}
          setSleepMinutes={setSleepMinutes}
          setLimitedSchedule={setLimitedSchedule}
        />
      </AlertSection>

      {/* THRESHOLD */}
      {!!showThreshold && (
        <Thresholds
          alertTypeId={alertTypeId}
          rules={rules}
          setRules={setRules}
          setThresholdIsValid={setThresholdIsValid}
          triedToSubmit={triedToSubmit}
          insectId={insectId}
          key={alertTypeId + insectId + rules.length} // to reset the component when alertTypeId or insectId changes
        />
      )}

      {/* COVERAGE */}
      {!!showCoverage && (
        <Coverage
          alertTypeId={alertTypeId}
          coverageBlocks={coverageBlocks}
          coverageIsValid={coverageIsValid}
          coverageProperties={coverageProperties}
          coverageStations={coverageStations}
          coverageType={coverageType}
          insectId={insectId}
          rules={rules}
          setCoverageBlocks={setCoverageBlocks}
          setCoverageIsValid={setCoverageIsValid}
          setCoverageProperties={setCoverageProperties}
          setCoverageStations={setCoverageStations}
          setCoverageType={setCoverageType}
          setRules={setRules}
          triedToSubmit={triedToSubmit}
        />
      )}

      {/* FREQUENCY */}
      {!!showFrequency && (
        <Frequency
          alertTypeId={alertTypeId}
          frequencyMinutes={frequencyMinutes}
          setFrequencyMinutes={setFrequencyMinutes}
        />
      )}
      {/* AUTOMATED SCHEDULING (degreeDays only) */}
      {!!showAutomatedScheduling && <AutomatedScheduling rulesHaveForecast={rulesHaveForecast} />}
      {/* SLEEP */}
      {!!showSleep && (
        <Sleep
          alertTypeId={alertTypeId}
          setSleep={setSleep}
          setSleepIsValid={setSleepIsValid}
          setSleepMinutes={setSleepMinutes}
          sleep={sleep}
          sleepMinutes={sleepMinutes}
          sleepUnits={sleepUnits}
          setSleepUnits={setSleepUnits}
          triedToSubmit={triedToSubmit}
        />
      )}

      {/* SCHEDULING */}
      {!!showScheduling && (
        <Scheduling
          alertTypeId={alertTypeId}
          endDate={endDate}
          limitedSchedule={limitedSchedule}
          setEndDate={setEndDate}
          setLimitedSchedule={setLimitedSchedule}
          setScheduleIsValid={setScheduleIsValid}
          setStartDate={setStartDate}
          startDate={startDate}
          triedToSubmit={triedToSubmit}
        />
      )}
      {/* CONTACTS */}
      {!!showContacts && (
        <Contacts
          contacts={contacts}
          contactsIsValid={contactsIsValid}
          groups={groups}
          notifyOwnerByEmail={notifyOwnerByEmail}
          notifyOwnerBySMS={notifyOwnerBySMS}
          setContacts={setContacts}
          setContactsIsValid={setContactsIsValid}
          setGroups={setGroups}
          setNotifyOwnerByEmail={setNotifyOwnerByEmail}
          setNotifyOwnerBySMS={setNotifyOwnerBySMS}
        />
      )}
      {/* NAME */}
      {!!showName && (
        <Name
          alertTypeId={alertTypeId}
          name={name}
          setName={setName}
          setNameIsValid={setNameIsValid}
          triedToSubmit={triedToSubmit}
        />
      )}
    </>
  )
}
